import Button from "@app/components/button";
import { addToast } from "@app/components/toast/toast.service";
import { RoutePath, SystemMessage } from "@app/constants";
import CampaignService from "@app/services/http/campaign.service";
import { LabelValue } from "@app/types";
import { createCampaignValidationSchema } from "@app/validations";
import { Form, Formik, FormikContextType, useFormikContext } from "formik";
import { createRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CampaignComponent from "./components/campaign.component";
import NotificationComponent from "./components/notification.component";
import BannerComponent from "./components/banner.component";

interface FormValues {
  name: string;
  discount: number;
  startDate: string;
  endDate: string;
  minPrice: number;
  maxPrice: number;

  // Campaign specific fields
  campaignBirthday: boolean;
  campaignUserType: LabelValue[];
  campaignUserListenedToMinutes: string;
  campaignKasIsGreaterThan: string;
  campaignKasIsLessThan: string;
  campaignHasPurchasedBooks: any[];
  campaignNotHasPurchasedBooks: any[];
  campaignTestingIds: string;

  // Notification specific fields
  notificationTitle: string;
  notificationBody: string;
  notificationNote: string;
  notificationRightIcon: string;
  notificationTimeSend: string;
  notificationRepeatDays: LabelValue[] | string[];
  isRepeatNotification: boolean;
  notificationBirthday: boolean;
  notificationUserType: LabelValue[];
  notificationUserListenedToMinutes: string;
  notificationKasIsGreaterThan: string;
  notificationKasIsLessThan: string;
  notificationHasPurchasedBooks: any[];
  notificationNotHasPurchasedBooks: any[];
  notificationTestingIds: string;

  // Banner specific fields
  content: string;
  titleButton: string;
  actionButton: string;
  totalDisplayCount: number;
  totalDisplayTime: number;
  bannerMedia: string;
  bannerBirthday: boolean;
  bannerUserType: LabelValue[];
  bannerUserListenedToMinutes: string;
  bannerKasIsGreaterThan: string;
  bannerKasIsLessThan: string;
  bannerHasPurchasedBooks: any[];
  bannerNotHasPurchasedBooks: any[];
  bannerTestingIds: string;
}

// Helper functions moved outside components for reuse
const hasCampaignData = (values: FormValues) => {
  return !!(
    values.name ||
    values.discount > 0 ||
    values.startDate ||
    values.endDate ||
    values.campaignBirthday ||
    (values.campaignUserType && values.campaignUserType.length > 0) ||
    values.minPrice > 0 ||
    values.maxPrice > 0 ||
    values.campaignUserListenedToMinutes ||
    values.campaignKasIsGreaterThan ||
    values.campaignKasIsLessThan ||
    (values.campaignHasPurchasedBooks &&
      values.campaignHasPurchasedBooks.length > 0) ||
    (values.campaignNotHasPurchasedBooks &&
      values.campaignNotHasPurchasedBooks.length > 0)
  );
};

const hasNotificationData = (values: FormValues) => {
  return !!(
    values.notificationTitle ||
    values.notificationBody ||
    values.notificationNote ||
    values.notificationRightIcon ||
    values.notificationTimeSend ||
    values.isRepeatNotification ||
    (values.notificationRepeatDays &&
      values.notificationRepeatDays.length > 0) ||
    values.notificationBirthday ||
    (values.notificationUserType && values.notificationUserType.length > 0) ||
    values.notificationUserListenedToMinutes ||
    values.notificationKasIsGreaterThan ||
    values.notificationKasIsLessThan ||
    (values.notificationHasPurchasedBooks &&
      values.notificationHasPurchasedBooks.length > 0) ||
    (values.notificationNotHasPurchasedBooks &&
      values.notificationNotHasPurchasedBooks.length > 0)
  );
};

const hasBannerData = (values: FormValues) => {
  return !!(
    values.content ||
    values.titleButton ||
    values.actionButton ||
    values.totalDisplayCount > 0 ||
    values.totalDisplayTime > 0 ||
    values.bannerMedia ||
    values.bannerBirthday ||
    (values.bannerUserType && values.bannerUserType.length > 0) ||
    values.bannerUserListenedToMinutes ||
    values.bannerKasIsGreaterThan ||
    values.bannerKasIsLessThan ||
    (values.bannerHasPurchasedBooks &&
      values.bannerHasPurchasedBooks.length > 0) ||
    (values.bannerNotHasPurchasedBooks &&
      values.bannerNotHasPurchasedBooks.length > 0)
  );
};

// Component to handle validation context updates
const ValidationContextUpdater = () => {
  const { values, setFieldValue } = useFormikContext<FormValues>();

  useEffect(() => {
    const newContext = {
      $hasCampaignData: hasCampaignData(values),
      $hasNotificationData: hasNotificationData(values),
      $hasBannerData: hasBannerData(values),
      $hasPriceRange: values.minPrice > 0 || values.maxPrice > 0,
    };

    // Update validation context
    setFieldValue("$hasCampaignData", newContext.$hasCampaignData);
    setFieldValue("$hasNotificationData", newContext.$hasNotificationData);
    setFieldValue("$hasBannerData", newContext.$hasBannerData);
    setFieldValue("$hasPriceRange", newContext.$hasPriceRange);
  }, [
    values.name,
    values.discount,
    values.startDate,
    values.endDate,
    values.minPrice,
    values.maxPrice,
    // Campaign fields
    values.campaignBirthday,
    values.campaignUserType,
    values.campaignUserListenedToMinutes,
    values.campaignKasIsGreaterThan,
    values.campaignKasIsLessThan,
    values.campaignHasPurchasedBooks,
    values.campaignNotHasPurchasedBooks,
    // Notification fields
    values.notificationTitle,
    values.notificationBody,
    values.notificationNote,
    values.notificationRightIcon,
    values.notificationTimeSend,
    values.isRepeatNotification,
    values.notificationRepeatDays,
    values.notificationBirthday,
    values.notificationUserType,
    values.notificationUserListenedToMinutes,
    values.notificationKasIsGreaterThan,
    values.notificationKasIsLessThan,
    values.notificationHasPurchasedBooks,
    values.notificationNotHasPurchasedBooks,
    // Banner fields
    values.content,
    values.titleButton,
    values.actionButton,
    values.totalDisplayCount,
    values.totalDisplayTime,
    values.bannerMedia,
    values.bannerBirthday,
    values.bannerUserType,
    values.bannerUserListenedToMinutes,
    values.bannerKasIsGreaterThan,
    values.bannerKasIsLessThan,
    values.bannerHasPurchasedBooks,
    values.bannerNotHasPurchasedBooks,
    setFieldValue,
  ]);

  return null; // This component doesn't render anything
};

const CreateCampaignPage = () => {
  const formRef = createRef<FormikContextType<FormValues>>();
  const navigate = useNavigate();

  const handleSubmit = async (values: FormValues) => {
    const payload: any = {};

    // Only add campaign if there's campaign data
    if (hasCampaignData(values)) {
      payload.campaign = {
        name: values.name || "",
        discount: values.discount || 0,
        startDate: values.startDate || "",
        endDate: values.endDate || "",
        testingIds: values.campaignTestingIds || "",
        targets: {
          birthday: !!values.campaignBirthday,
          userType:
            (values.campaignUserType as any).length > 0
              ? (values.campaignUserType as any).map(
                  (item: LabelValue) => item.value
                )
              : ["ALL"],
          ...(values.minPrice > 0 || values.maxPrice > 0
            ? {
                rangePrice: [
                  Number(values.minPrice || 0),
                  Number(values.maxPrice || 0),
                ],
              }
            : {}),
          userListenedToMinutes: Number(
            values.campaignUserListenedToMinutes || 0
          ),
          kasIsGreaterThan: Number(values.campaignKasIsGreaterThan || 0),
          kasIsLessThan: Number(values.campaignKasIsLessThan || 0),
          hasPurchasedBooks: (values.campaignHasPurchasedBooks || []).map(
            (it: any) => Number(it.value ?? it)
          ),
          notHasPurchasedBooks: (values.campaignNotHasPurchasedBooks || []).map(
            (it: any) => Number(it.value ?? it)
          ),
        },
      };
    }

    // Only add notification if there's notification data
    if (hasNotificationData(values)) {
      payload.notification = {
        title: values.notificationTitle || "",
        body: values.notificationBody || "",
        note: values.notificationNote || "",
        rightIcon: values.notificationRightIcon || "",
        timeSend: values.notificationTimeSend || "",
        repeatDays: Array.isArray(values.notificationRepeatDays)
          ? (values.notificationRepeatDays as any[]).map((d: any) =>
              typeof d === "string" ? d : d.value
            )
          : [],
        testingIds: values.notificationTestingIds || "",
        // Pass user targeting from UI for notifications
        targets: {
          birthday: !!values.notificationBirthday,
          userType:
            (values.notificationUserType as any).length > 0
              ? (values.notificationUserType as any).map(
                  (item: LabelValue) => item.value
                )
              : ["ALL"],
          userListenedToMinutes: Number(
            values.notificationUserListenedToMinutes || 0
          ),
          kasIsGreaterThan: Number(values.notificationKasIsGreaterThan || 0),
          kasIsLessThan: Number(values.notificationKasIsLessThan || 0),
          hasPurchasedBooks: (values.notificationHasPurchasedBooks || []).map(
            (it: any) => Number(it.value ?? it)
          ),
          notHasPurchasedBooks: (
            values.notificationNotHasPurchasedBooks || []
          ).map((it: any) => Number(it.value ?? it)),
        },
      };
    }

    // Only add popupBanner if there's banner data
    if (hasBannerData(values)) {
      payload.popupBanner = {
        content: values.content || "",
        userType: Number(
          Array.isArray(values.bannerUserType) &&
            values.bannerUserType.length > 0
            ? (values.bannerUserType[0] as LabelValue).value
            : 0
        ),
        media: values.bannerMedia || "",
        startDate: values.startDate || "",
        endDate: values.endDate || "",
        buttonTitle: values.titleButton || "",
        buttonAction: values.actionButton || "",
        totalDisplayCount: Number(values.totalDisplayCount || 0),
        totalDisplayTime: Number(values.totalDisplayTime || 0),
        testingIds: values.bannerTestingIds || "",
        // Pass user targeting from UI for popup banner
        targets: {
          birthday: !!values.bannerBirthday,
          userType:
            (values.bannerUserType as any).length > 0
              ? (values.bannerUserType as any).map(
                  (item: LabelValue) => item.value
                )
              : ["ALL"],
          userListenedToMinutes: Number(
            values.bannerUserListenedToMinutes || 0
          ),
          kasIsGreaterThan: Number(values.bannerKasIsGreaterThan || 0),
          kasIsLessThan: Number(values.bannerKasIsLessThan || 0),
          hasPurchasedBooks: (values.bannerHasPurchasedBooks || []).map(
            (it: any) => Number(it.value ?? it)
          ),
          notHasPurchasedBooks: (values.bannerNotHasPurchasedBooks || []).map(
            (it: any) => Number(it.value ?? it)
          ),
        },
      };
    }

    // Check if payload has any data
    if (Object.keys(payload).length === 0) {
      addToast({
        text: "Vui lòng nhập ít nhất một loại dữ liệu (Campaign, Notification hoặc Banner)",
      });

      return;
    }

    await CampaignService.create(payload);
    addToast({ text: SystemMessage.ADD_SUCCESSFULLY });
    navigate(`/${RoutePath.CAMPAIGN_MANAGEMENT}`);
  };

  return (
    <div className="py-6 px-12">
      <Formik
        innerRef={formRef}
        initialValues={{
          name: "",
          discount: 0,
          startDate: "",
          endDate: "",
          minPrice: 0,
          maxPrice: 0,

          // Campaign specific fields
          campaignBirthday: false,
          campaignUserType: [],
          campaignUserListenedToMinutes: "",
          campaignKasIsGreaterThan: "",
          campaignKasIsLessThan: "",
          campaignHasPurchasedBooks: [],
          campaignNotHasPurchasedBooks: [],
          campaignTestingIds: "",

          // Notification specific fields
          notificationTitle: "",
          notificationBody: "",
          notificationNote: "",
          notificationRightIcon: "",
          notificationTimeSend: "",
          notificationRepeatDays: [],
          isRepeatNotification: false,
          notificationBirthday: false,
          notificationUserType: [],
          notificationUserListenedToMinutes: "",
          notificationKasIsGreaterThan: "",
          notificationKasIsLessThan: "",
          notificationHasPurchasedBooks: [],
          notificationNotHasPurchasedBooks: [],
          notificationTestingIds: "",

          // Banner specific fields
          content: "",
          titleButton: "",
          actionButton: "",
          totalDisplayCount: 0,
          totalDisplayTime: 0,
          bannerMedia: "",
          bannerBirthday: false,
          bannerUserType: [],
          bannerUserListenedToMinutes: "",
          bannerKasIsGreaterThan: "",
          bannerKasIsLessThan: "",
          bannerHasPurchasedBooks: [],
          bannerNotHasPurchasedBooks: [],
          bannerTestingIds: "",
        }}
        onSubmit={handleSubmit}
        validationSchema={createCampaignValidationSchema}
        validateOnChange
        validateOnBlur
        validateOnMount={false}
      >
        <Form>
          <ValidationContextUpdater />
          <div className="mt-6">
            <div className="grid grid-cols-2 gap-4">
              {/* Campaign */}
              <CampaignComponent />

              {/* Notification */}
              <NotificationComponent />
            </div>
          </div>
          <div className="mt-6">
            {/* Banner */}
            <BannerComponent />
          </div>
          <div className="mt-6 flex justify-left gap-4">
            <Button
              type="submit"
              size="xs"
              label={"Tạo"}
              width={180}
              className="!h-[51px] !leading-[51px]"
              labelClassName="text-xl"
            />
            <Button
              size="xs"
              label="Hủy"
              theme="secondary"
              width={180}
              className="!h-[51px] !leading-[51px]"
              labelClassName="text-xl"
            />
          </div>
        </Form>
      </Formik>
    </div>
  );
};

export default CreateCampaignPage;
